import { useMemo, useRef, type ReactNode } from 'react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { Input } from '@/UI/Input/Input';
import { UnitInput, type UnitInputProps } from '@/UI/UnitInput/UnitInput';
import { useNumberInput } from '@/hooks/useNumberInput';
import { Box } from '@/UI/Box/Box';

interface NumberInputProps {
  value: number;
  onSubmit: (value: number) => void;
  parse?: (value: number) => number;
  format?: (value: number) => number;
  decimals?: 0 | 1 | 2;
  disabled: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  unit?: string;
  blurOnEnter?: boolean;
  placeholder?: string;
  commitOnlyWhenEdited?: boolean;
  /** Any CSS width. Defaults to the 88px this input has always rendered at. */
  width?: string;
  ariaLabel?: string;
  disabledTextColor?: string;
}

export function NumberInput({
  value,
  onSubmit,
  parse,
  format,
  disabled,
  suffix,
  decimals = 2,
  prefix,
  unit,
  blurOnEnter,
  placeholder,
  commitOnlyWhenEdited,
  width = '88px',
  ariaLabel,
  disabledTextColor,
}: NumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { handleBlur, handleFocus, handleKeyDown, handleChange, displayedValue } = useNumberInput({
    inputRef,
    value,
    onSubmit,
    parse,
    format,
    decimals,
    blurOnEnter,
    placeholder,
    commitOnlyWhenEdited,
  });

  const isMixedIdle = placeholder !== undefined && displayedValue === String(value);
  const shownValue = isMixedIdle ? '' : displayedValue;

  const inputProps = useMemo<Omit<UnitInputProps, 'unit' | 'value'>>(() => {
    return {
      disabled,
      size: 'small',
      onChange: handleChange,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onKeyDownCapture: handleKeyDown,
      placeholder,
      startAdornment: prefix ? (
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <TextSmRg
            sx={{ lineHeight: '1rem', height: '1rem' }}
            color={disabled && disabledTextColor ? disabledTextColor : token.textSecondary}
          >
            {prefix}
          </TextSmRg>
        </Box>
      ) : null,
      endAdornment: suffix ? (
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <TextSmRg
            sx={{ lineHeight: '1rem', height: '1rem' }}
            color={disabled && disabledTextColor ? disabledTextColor : token.textSecondary}
          >
            {suffix}
          </TextSmRg>
        </Box>
      ) : null,
      inputRef,
      'aria-autocomplete': 'none',
      autoComplete: 'off',
      className: 'nodrag',
      inputProps: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
      sx: {
        width,
        ...(disabled && disabledTextColor
          ? {
              '& .MuiInputBase-root.Mui-disabled': {
                color: disabledTextColor,
              },
            }
          : {}),
      },
    };
  }, [
    disabled,
    disabledTextColor,
    handleChange,
    handleBlur,
    handleFocus,
    handleKeyDown,
    prefix,
    suffix,
    placeholder,
    width,
    ariaLabel,
  ]);

  return unit ? (
    <UnitInput {...inputProps} unit={unit} value={isMixedIdle ? undefined : Number(displayedValue)} />
  ) : (
    <Input {...inputProps} value={shownValue} />
  );
}
